/**
 * Transfer a prospect into the Recruiting Table (creates a recruit at
 * 'Inquiry'). The recruiting form (recruitmentFormSchema) requires state
 * licenses, broker commission split, and a recruiter even though those recruit
 * columns are nullable — so we collect/confirm them here, pre-filled from the
 * prospect, and block until valid. Position is coerced to a valid
 * RecruitPosition.
 */
import React, { useState } from 'react';
import { z } from 'zod';
import { LoDialogDescription, LoDialogShell, LoDialogTitle } from './LoRadixOverlay';
import { recruitmentFormSchema, type RecruitmentFormValues } from '@/components/recruitment/validations';
import type { LoProspect } from '@/types/lo-prospect';
import type { RecruiterOption } from '@/services/loProspectService';

// The shared recruitmentFormSchema leaves city/state/DRE optional (normal
// recruiting). Transferring an LO prospect requires the full profile, so layer
// transfer-only checks on top WITHOUT changing the shared schema: city + state,
// and a DRE number when the prospect is in California (mailing state or a CA license).
const transferSchema = recruitmentFormSchema.superRefine((v, ctx) => {
  if (!v.mailingCity?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mailingCity'], message: 'City is required to transfer' });
  }
  if (!v.mailingState?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mailingState'], message: 'State is required to transfer' });
  }
  const isCA =
    v.mailingState?.trim().toUpperCase() === 'CA' ||
    (v.stateLicenses ?? []).some((s) => s.trim().toUpperCase() === 'CA');
  if (isCA && !v.dreNumber?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dreNumber'], message: 'A DRE number is required for California' });
  }
});

const RECRUIT_POSITIONS = ['Loan Officer', 'Senior Loan Officer', 'Branch Manager'] as const;

function coercePosition(p: string | null): (typeof RECRUIT_POSITIONS)[number] {
  return (RECRUIT_POSITIONS as readonly string[]).includes(p ?? '')
    ? (p as (typeof RECRUIT_POSITIONS)[number])
    : 'Loan Officer';
}

export function TransferDialog({
  prospect,
  recruiters,
  busy,
  onClose,
  onSubmit
}: {
  prospect: LoProspect;
  recruiters: RecruiterOption[];
  busy?: boolean;
  onClose: () => void;
  onSubmit: (values: RecruitmentFormValues) => void;
}) {
  const [firstName, setFirstName] = useState(prospect.firstName);
  const [lastName, setLastName] = useState(prospect.lastName);
  const [email, setEmail] = useState(prospect.email ?? '');
  const [phone, setPhone] = useState(prospect.phone ?? '');
  const [position, setPosition] = useState<string>(coercePosition(prospect.position));
  const [recruiterId, setRecruiterId] = useState(prospect.recruiterId ?? '');
  const [brokerSplit, setBrokerSplit] = useState('');
  const [bankedComp, setBankedComp] = useState('');
  const [city, setCity] = useState(prospect.city ?? '');
  const [stateCode, setStateCode] = useState(prospect.state ?? '');
  const [dre, setDre] = useState(prospect.dre ?? '');
  const [licenses, setLicenses] = useState<string[]>([...prospect.licenses]);
  const [licInput, setLicInput] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const addLic = (raw: string) => {
    const code = raw.trim().toUpperCase();
    if (code && !licenses.includes(code)) {
      setLicenses((s) => [...s, code]);
    }
    setLicInput('');
  };

  const isCA =
    stateCode.trim().toUpperCase() === 'CA' || licenses.some((s) => s.trim().toUpperCase() === 'CA');

  const submit = () => {
    const raw: Record<string, unknown> = {
      firstName,
      lastName,
      email,
      phone,
      nmlsNumber: prospect.nmls,
      mailingCity: city,
      mailingState: stateCode,
      dreNumber: dre || undefined,
      position,
      stateLicenses: licenses,
      brokerCommissionSplit: brokerSplit,
      bankedCompensation: bankedComp || undefined,
      status: 'Inquiry',
      recruiterId
    };
    const parsed = transferSchema.safeParse(raw);
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onSubmit(parsed.data);
  };

  return (
    <LoDialogShell onClose={onClose} cardClassName="wide">
          <div className="modal-head">
            <div className="modal-eyebrow">Transfer to Recruiting</div>
            <LoDialogTitle>Move {prospect.firstName} into Recruiting</LoDialogTitle>
            <LoDialogDescription>
              Creates a recruit at <b>Inquiry</b>. Confirm the details the recruiting workflow requires.
            </LoDialogDescription>
          </div>
          <div className="modal-body">
            <div className="dw-grid">
              <div className="field">
                <label>First name</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="field">
                <label>Last name</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <div className="field">
                <label>Position</label>
                <select value={position} onChange={(e) => setPosition(e.target.value)}>
                  {RECRUIT_POSITIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field span2">
                <label>Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="field">
                <label>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="field">
                <label>NMLS #</label>
                <input value={prospect.nmls} readOnly />
              </div>
              <div className="field">
                <label>City</label>
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Scottsdale" />
              </div>
              <div className="field">
                <label>State</label>
                <input value={stateCode} onChange={(e) => setStateCode(e.target.value)} maxLength={2} placeholder="AZ" />
              </div>
              <div className="field">
                <label>
                  DRE #{isCA && <span className="hint"> (required for CA)</span>}
                </label>
                <input value={dre} onChange={(e) => setDre(e.target.value)} placeholder="01234567" />
              </div>
              <div className="field">
                <label>Recruiter</label>
                <select value={recruiterId} onChange={(e) => setRecruiterId(e.target.value)}>
                  <option value="">Select…</option>
                  {recruiters.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Broker commission split %</label>
                <input value={brokerSplit} onChange={(e) => setBrokerSplit(e.target.value)} inputMode="decimal" placeholder="e.g. 70" />
              </div>
              <div className="field">
                <label>
                  Banked compensation % <span className="hint">(optional)</span>
                </label>
                <input value={bankedComp} onChange={(e) => setBankedComp(e.target.value)} inputMode="decimal" />
              </div>
              <div className="field full">
                <label>State licenses</label>
                <div className="lic-edit">
                  {licenses.map((c) => (
                    <span className="lic-chip" key={c}>
                      {c}
                      <button onClick={() => setLicenses((s) => s.filter((x) => x !== c))} aria-label={`Remove ${c}`} type="button">
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    className="lic-add"
                    value={licInput}
                    placeholder="+ add"
                    maxLength={2}
                    onChange={(e) => setLicInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addLic(licInput);
                      }
                    }}
                    onBlur={() => licInput && addLic(licInput)}
                  />
                </div>
              </div>
            </div>

            {errors.length > 0 && (
              <div className="err-box">
                <div>
                  <div className="err-title">Please complete the required fields</div>
                  <div className="err-detail">{errors.join(' · ')}</div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-foot">
            <button className="btn-sm" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="btn-sm solid" onClick={submit} disabled={busy} type="button">
              {busy ? 'Transferring…' : 'Transfer to Recruiting'}
            </button>
          </div>
    </LoDialogShell>
  );
}
